import { DraftZoc } from '@/app/context/draftingContext';
import type { Zoc } from '@/app/_lib/zoc';
import axios from 'axios';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

export const getZocListKey = () => {
  return `/api/zoc`;
};

export const getZocShareKey = () => {
  return `/api/share`;
};

export const getAcceptZocShareKey = (shareCode: string) => {
  return `/api/share/accept/${shareCode}`;
};

export const getZocDetailKey = (zocId: string, languageCode?: string) => {
  return `/api/zoc/${zocId}/${languageCode}`;
};

export const getZocExportKey = (zocId: string, languageCode?: string) => {
  const q = !!languageCode ? `language_code=${languageCode}` : '';
  return `/api/zoc/${zocId}/export${q}`;
};

export const useGetZocDetail = (zocId: string, languageCode: string) => {
  return useSWR(zocId ? getZocDetailKey(zocId, languageCode) : null, () =>
    axios
      .get(`/api/zoc/${zocId}?language=${languageCode}`)
      .then(({ data }) => data as Zoc)
  );
};

export const useShareZoc = (zocId: string) => {
  return useSWR(getZocShareKey(), () =>
    axios.post(`/api/share/`, { zoc_id: zocId }).then(({ data }) => data)
  );
};

export const useExportZoc = (
  zocId: string,
  languageCode: string,
  disabled: boolean
) => {
  return useSWR(
    disabled ? null : getZocExportKey(zocId, languageCode),
    () =>
      axios
        .get(`/api/zoc/${zocId}/export?language=${languageCode}`)
        .then(({ data }) => data.pdf_url as string),
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    }
  );
};

export const useGoogleExportZoc = (selectedLanguage: string) => {
  return useSWRMutation(
    getZocExportKey('zocId'),
    (
      _: string,
      {
        arg: { zocId, googleToken },
      }: { arg: { zocId: string; googleToken: string } }
    ) =>
      axios
        .post(`/api/zoc/${zocId}/export`, {
          'google_token': googleToken,
        })
        .then(({ data }) => data.google_response.documents)
  );
};

export const useUpdateZocMutation = (zocId: string) => {
  return useSWRMutation(
    getZocDetailKey(zocId),
    (_: string, { arg: zoc }: { arg: Partial<Zoc> }) =>
      axios.patch(`/api/zoc/${zocId}`, zoc)
  );
};

export const useAcceptShareZoc = (shareCode: string) => {
  return useSWRMutation(getAcceptZocShareKey(shareCode), () =>
    axios.post(`/api/share/accept/${shareCode}`)
  );
};

export const useDeleteZocMutation = (zocId: string) => {
  return useSWRMutation(getZocDetailKey(zocId), () =>
    axios.delete(`/api/zoc/${zocId}`)
  );
};

export const useLoadDraftMutation = () => {
  return useSWRMutation(`/zoc/draft`, (_: string, { arg }: { arg: any }) =>
    axios.post('/api/zoc/draft', arg).then(({ data }) => data as DraftZoc)
  );
};
